import { STARTING_LEVEL, type SkillLevels } from './maths-skills.js'
import { type MastermindSize } from './mastermind-score.js'

/**
 * What she is getting better at, in Chaka's Mastermind.
 *
 * **A skill is a size**, exactly as in Stripey's Sudoku and Maritza's Mosaic,
 * and for the same reason: a round is one hidden row for ten minutes, and what
 * she meets in it is whatever deductions that row happens to want, in an order
 * nobody chose. The size is what she picked and what she spent the round on.
 *
 * Not a teddy, either. Recording `nemo` would say she is good at clownfish.
 *
 * The step from "middling" to "the proper one" is the one that matters, and it
 * is why they are separate skills rather than one with a level: allowing a
 * repeat is not more of the same reasoning, it is the removal of the rule she
 * has been reasoning with. Up to then, settling a teddy rules it out
 * everywhere else; after it, nothing can ever be ruled out. A level that slid
 * between those two would be a level that meant a different thing at each end.
 */
export interface MastermindSkill {
  readonly key: string
  /** Shown to a parent, not to the player. */
  readonly label: string
  readonly size: MastermindSize
}

/*
 * The `mastermind.` these start with is the game key, spelt out rather than
 * built from one. `sudoku-skills.ts` says why and it is worth repeating: these
 * are strings in a database, and deriving them would mean renaming a game
 * could silently orphan a year of her levels.
 */
export const MASTERMIND_SKILLS: readonly MastermindSkill[] = [
  { key: 'mastermind.gentle', label: 'Four teddies, none repeated', size: 'gentle' },
  { key: 'mastermind.middling', label: 'Five teddies, none repeated', size: 'middling' },
  { key: 'mastermind.proper', label: 'Five teddies, repeats allowed', size: 'proper' },
]

export function mastermindSkillOf(size: MastermindSize): MastermindSkill {
  const skill = MASTERMIND_SKILLS.find((candidate) => candidate.size === size)
  // The list above is exhaustive over MastermindSize by construction and a
  // test holds that, so reaching here is a missing entry rather than bad input.
  if (!skill) throw new Error(`No skill for a ${size} row`)

  return skill
}

/** Where she stands at this size, or the starting level if she has never played it. */
export function mastermindLevelFor(levels: SkillLevels, size: MastermindSize): number {
  return levels[mastermindSkillOf(size).key] ?? STARTING_LEVEL
}
